import { useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { COURT_FINDER_PAGE, getCourtPageUrl } from 'constants/pages';
import { getVenuePrimarySport } from 'constants/venues';
import { SportsEnum, VenueAccessTypesEnum, VenueNetsEnum } from 'types/generated/client';
import { ButtonLink } from 'components/Button';
import Skeleton from 'components/LoadingSkeleton';
import { BookableVenueCard, DateStrip, getTodayLocal } from 'components/courtBooking';
import { useFeedAvailability } from './useFeedAvailability';

const FEED_TIMEZONE = 'America/New_York';

export interface BookableFeedVenue {
  id: string;
  title: string;
  slug: string;
  addressString: string;
  accessType?: VenueAccessTypesEnum | null;
  indoorCourtCount?: number | null;
  outdoorCourtCount?: number | null;
  pickleballNets?: VenueNetsEnum | null;
  hasPickleball?: boolean | null;
  hasTennis?: boolean | null;
  hasPadel?: boolean | null;
  images: { id: string; url: string; isVisible?: boolean | null }[];
}

interface Props {
  venues: BookableFeedVenue[];
  loading?: boolean;
}

/**
 * The home feed when the player's intent is "Book a court": a day strip over the
 * clubs nearby and the times each can actually take.
 *
 * Availability is the club's real free time for the chosen day, and only clubs
 * live on Bounce get a card. Picking a time deep-links to the club page, which
 * owns the booking flow.
 */
export default function BookCourtFeed({ venues, loading }: Props) {
  const router = useRouter();

  const venueIds = useMemo(() => venues.map((venue) => venue.id), [venues]);
  const [selectedDate, setSelectedDate] = useState('');
  const { dates, byVenue, isLoading } = useFeedAvailability(
    venueIds,
    selectedDate || getTodayLocal(FEED_TIMEZONE),
  );
  const activeDate = selectedDate || dates[0]?.localDate || '';

  const entries = useMemo(
    () =>
      venues.flatMap((venue) => {
        const availability = byVenue[venue.id];
        return availability ? [{ venue, availability }] : [];
      }),
    [venues, byVenue],
  );

  const handleSlotSelect = (venueId: string, slotId: string) => {
    const slug = venues.find((venue) => venue.id === venueId)?.slug;
    if (slug) {
      router.push(`${getCourtPageUrl(slug)}?date=${activeDate}&slot=${slotId}`);
    }
  };

  if (loading || (isLoading && entries.length === 0)) {
    return (
      <div className="flex flex-col gap-ds-xl">
        <Skeleton height="3rem" borderRadius="1.5rem" />
        <Skeleton height="24rem" borderRadius="1rem" />
        <Skeleton height="12.5rem" borderRadius="1rem" />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <p className="typography-product-text-card-desktop py-ds-2xl text-center text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
        No clubs near you are taking bookings yet.
      </p>
    );
  }

  const [featured, ...rest] = entries;

  const cardProps = ({ venue, availability }: (typeof entries)[number]) => ({
    venueId: venue.id,
    slug: venue.slug,
    title: venue.title,
    imageUrl: (venue.images.find((image) => image.isVisible) ?? venue.images[0])?.url,
    addressString: venue.addressString,
    accessType: venue.accessType,
    totalCourtCount: (venue.indoorCourtCount ?? 0) + (venue.outdoorCourtCount ?? 0),
    pickleballNets: venue.pickleballNets,
    primarySport: getVenuePrimarySport(venue) as SportsEnum | null,
    slots: availability.slots,
    onSlotSelect: handleSlotSelect,
  });

  return (
    <div className="flex flex-col gap-ds-xl">
      <DateStrip dates={dates} selectedDate={activeDate} onDateChange={setSelectedDate} />

      <BookableVenueCard
        {...cardProps(featured)}
        isFeatured
        fromPriceUnitAmount={featured.availability.fromPriceUnitAmount}
        currency={featured.availability.currency}
      />

      {rest.length > 0 && (
        // Mobile stacks the clubs behind dividers; desktop lays them out four up.
        <div className="grid gap-ds-xl lg:grid-cols-4 lg:gap-x-ds-xl lg:gap-y-10">
          {rest.map((entry, index) => (
            <BookableVenueCard
              key={entry.venue.id}
              {...cardProps(entry)}
              className={
                index > 0
                  ? 'max-lg:border-t max-lg:border-color-border-card-lightmode max-lg:pt-ds-xl max-lg:dark:border-color-border-card-darkmode'
                  : undefined
              }
            />
          ))}
        </div>
      )}

      <ButtonLink
        href={COURT_FINDER_PAGE}
        variant="secondary"
        size="md"
        isInline
        label="Explore all"
        className="self-center shadow-fab"
      />
    </div>
  );
}
